/**
 * This component is used to validate the auth values.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import jwt from "jwt-decode";
import MemoryType from "../../../service/memory/enum/MemoryType";
import IntentType from "../../../intentexecutor/utils/IntentType";
import { useSetRecoilState } from "recoil";
import { userDetails } from "../../../components/recoil/userDataRecoil";
import { useWriteMemory } from "../../generic/hooks/useWriteMemory";
import { executeIntent } from "../../utils/IntentExecutor";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import configurationKeys from "../../../service/memory/recoil/featurekeys";
import { publishEvent } from "../../../components/generic/EventPublisher";
import { Spin } from "antd";
import { startAuthentication } from "@simplewebauthn/browser";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import PinOtpComponent from "../login/PinOtpComponent";

const AuthValidator = () => {
  const setUserInformation = useSetRecoilState(userDetails);
  const navigate = useNavigate();
  const setMemory = useWriteMemory();
  const setConfiguration = useSetRecoilState(currentConfiguration);
  const challenge = useReadMemory(MemoryType.CHALLENGE);
  const [userJson, setUserJson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPinComponent, setShowPinComponent] = useState(false);

  const getTokenData = () => {
    const hash = window.location.hash.substring(1); // Remove the '#' from the start of the hash
    const params = new URLSearchParams(hash);
    return params.get("id_token") || params.get("tokeninfo"); // Assuming the token is stored under the 'id_token' key
  };

  const loadConfiguration = async () => {
    try {
      const payload = {
        configurationKey: configurationKeys,
      };
      const response = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload
      );
      const configurationKeysAndValue = {};
      // To ensure that only the configuration has been returned from the configuration store the if
      // condition is present.
      if (configurationKeys.length === response.length) {
        for (const item of response) {
          const configurationValue = item?.configurationValue;
          configurationKeysAndValue[item?.configurationKey] =
            JSON.parse(configurationValue);
        }
        setConfiguration(configurationKeysAndValue);
      }
    } catch (ignored) {}
  };

  const publishFailedLogin = (user, details = {}) => {
    publishEvent("niramaya.user.login.failed", user, {
      userIdentifier: user?.userIdentifier,
      email: user?.email,
      ...details,
    });
  };

  const redirectUser = async (user) => {
    setMemory(MemoryType.USER, user);
    setMemory(MemoryType.AUTH, user);
    publishEvent("niramaya.user.login", user);
    sessionStorage.setItem("sidebarItemSelected", "Dashboard");
    setUserInformation({ ...user });
    await loadConfiguration();

    switch (user?.jobTitle) {
      case "Volunteer":
        navigate("/careprovider/homepage", { replace: true });
        break;
      case "Volunteer Coordinator":
        navigate("/carecoordinator/homepage", { replace: true });
        break;
      case "Billing Admin":
        navigate("/billingAdmin/billing", { replace: true });
        break;
      default:
        navigate("/error?code=1004");
        break;
    }
  };

  const handleWebAuthn = async (user) => {
    try {
      const challenge = JSON.parse(localStorage.getItem(MemoryType.CHALLENGE));

      const options = {
        challenge: btoa(crypto.randomUUID()),
        rpId: window.location.hostname,
        userVerification: "required",
        allowCredentials: [
          {
            id: challenge.id,
            type: "public-key",
          },
        ],
      };

      const authResult = await startAuthentication(options);
      return authResult;
    } catch (error) {
      setLoading(false);
      setShowPinComponent(true);
    }
  };

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes("error")) {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    const getUserDetails = async () => {
      setLoading(true);
      let email;
      let user;
      try {
        const token = getTokenData();
        // Storing token data in local storage data
        setMemory(MemoryType.TOKEN, token, null);
        email = jwt(token).emails[0];
        const role = jwt(token)?.jobTitle;

        if (!role) {
          publishFailedLogin({ email }, {
            email,
            reason: "missing_role",
            errorCode: "1004",
          });
          navigate("/error?code=1004");
        } else {
          const payload = {
            email,
          };
          const userData = await executeIntent(
            IntentType.GET_WORKFORCE_BY_EMAIL,
            payload
          );
          user = userData[0];

          if (!user?.userIdentifier) {
            publishFailedLogin(user || { email }, {
              email,
              reason: "user_not_found",
              errorCode: "1007",
            });
            navigate("/error?code=1007");
            return;
          }
          setUserJson(userData[0]);

          if (!challenge || challenge?.userIdentifier !== user?.userIdentifier) {
            setLoading(false);
            setShowPinComponent(true);
            return;
          }

          const isAuthenticated = await handleWebAuthn(user);
          if (isAuthenticated) {
            await redirectUser(user);
          } else if (user?.hasPin) {
            setLoading(false);
            setShowPinComponent(true);
          } else {
            publishFailedLogin(user, {
              email: user?.email || email,
              reason: "authentication_failed",
              errorCode: "1005",
            });
            navigate("/error?code=1005");
          }
        }
      } catch (e) {
        const hash = window.location.hash;
        if (!(hash && hash.includes("error"))) {
          publishFailedLogin(user || (email ? { email } : null), {
            email,
            reason: "login_exception",
            errorCode: "1007",
          });
        }
        navigate("/error?code=1007");
      }
    };

    getUserDetails();
  }, []);

  return (
    <>
      {loading ? (
        <Spin size="large" className="spinnerCenter" />
      ) : (
        showPinComponent && <PinOtpComponent user={userJson} />
      )}
    </>
  );
};

export default AuthValidator;
